import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgbModule, NgbCarouselConfig, NgbModalConfig, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { AlertComponent } from './_directives/alert.component';
import { AlertService } from './_services/alert.service';
import { AuthService } from './_services/auth.service';

// Routing
import { routing } from './app.routing';

// Components
import { AppComponent } from './app.component';

import { SettingsModule } from './_layout/settings/settings.module';
import { ThemeSettingsConfig } from './_layout/settings/theme-settings.config';
import { MenuSettingsConfig } from './_layout/settings/menu-settings.config';

import { HeaderComponent } from './_layout/header/header.component';
import { VerticalComponent as HeaderVerticalComponent } from './_layout/header/vertical/vertical.component';
import { HorizontalComponent as HeaderHorizontalComponent } from './_layout/header/horizontal/horizontal.component';
import { FullLayoutNavbarComponent } from './_layout/header/full-layout-navbar/full-layout-navbar.component';

import { FooterComponent } from './_layout/footer/footer.component';
import { NavigationComponent as AppNavigationComponent } from './_layout/navigation/navigation.component';

import { PublicLayoutComponent } from './_layout/public-layout/public-layout.component';
import { PrivateLayoutComponent } from './_layout/private-layout/private-layout.component';

import { RegisterComponent } from './register';
import { SocialSigninComponent } from './social-signin/social-signin.component';
import { LoginComponent } from './login';

import { ChangelogComponent } from './changelog/changelog.component';

import { NavbarService } from './_services/navbar.service';
import { VerticalnavComponent } from './_layout/navigation/verticalnav/verticalnav.component';
import { HorizontalnavComponent } from './_layout/navigation/horizontalnav/horizontalnav.component';

// perfect scroll bar
// spinner
import { NgxSpinnerModule } from 'ngx-spinner';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { DeviceDetectorService } from './_services/device-detector.service';
import { RouterModule } from '@angular/router';
import { CustomizerComponent } from './_layout/customizer/customizer.component';
import { PartialsModule } from './content/partials/partials.module';
import { BreadcrumbModule } from './_layout/breadcrumb/breadcrumb.module';
import { DataApiService } from './_services/data.api';
import { HorizontalCustomizerComponent } from './_layout/customizer/horizontal-customizer/horizontal-customizer.component';
import { BlockTemplateComponent } from './_layout/blockui/block-template.component';
import { BlockUIModule } from 'ng-block-ui';
import { MatchHeightModule } from './content/partials/general/match-height/match-height.module';
import { FullLayoutComponent } from './_layout/full-layout/full-layout.component';
import { ViewProductComponent } from './pages/product-management/view-product/view-product.component';
import { AddProductComponent } from './pages/product-management/add-product/add-product.component';
import { ProductListComponent } from './pages/product-management/product-list/product-list.component';

import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { CategoryListComponent } from './pages/category-management/category-list/category-list.component';
import { AddCategoryComponent } from './pages/category-management/add-category/add-category.component';
import { ViewCategoryComponent } from './pages/category-management/view-category/view-category.component';
import { ListRetailerComponent } from './pages/retailer-management/list-retailer/list-retailer.component';
import { AddRetailerComponent } from './pages/retailer-management/add-retailer/add-retailer.component';
import { FormModalComponent } from './pages/modal/form-modal/form-modal.component';
import { UserListComponent } from './pages/users/user-list/user-list.component';
import { OfferListComponent } from './pages/offer-management/offer-list/offer-list.component';
import { AddOfferComponent } from './pages/offer-management/add-offer/add-offer.component';
import { ImageUploadComponent } from './pages/resuable/upload/image-upload/image-upload.component';
import { ToastrModule } from 'ngx-toastr';
import { MonthlyCreateComponent } from './pages/monthly-pass/monthly-create/monthly-create.component';
import { MonthlyListComponent } from './pages/monthly-pass/monthly-list/monthly-list.component';
import { TableViewComponent } from './shared/table-view/table-view.component';
import { RatingComponent } from './shared/rating/rating.component';
import { AuthPermissionDirective } from './directive/auth/auth-permission.directive';
import { OfferSectionComponent } from './pages/offer-management/offer-section/offer-section.component';
import { AuthInterceptor } from './services/auth/auth.interceptor';
import { PageAuthGuard } from './services/auth/page-auth.guard';
import { AuthGuard } from './services/auth/auth.guard';
import { AuthGuardUser } from './_guards/auth.guard';
import { AuthServiceUser } from './services/auth/auth.service';
import { GoogleGeolocationComponent } from "./shared/google-geolocation/google-geolocation.component";
import { GoogleMapComponent } from "./shared/google-map/google-map.component";
import { AuthGuardCheck } from './services/auth/authguardCheck.guard';
import { PageLoaderComponent } from './shared/page-loader/page-loader.component';
import { MediaPreviewComponent } from './shared/media-preview.component';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { RestrictToNumbersDirective } from './services/restrict-numers-count/restrictToNumbers.directive';
import { UrlValidatorDirective } from './services/url-validator.directive';


import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { FocusOnClickDirective } from './services/focus-on-click.directive';


export function playerFactory() {
  return player;
}

export function initializeAuth(authService: AuthServiceUser): () => void {
  return () => {
    const isLoggedIn = authService.isUserAuthenticated();
    console.log('User is authenticated:', isLoggedIn);
    // Optionally, restore user state or validate the token with a backend API
  };
}

@NgModule({
  imports: [
    BrowserModule,
    CommonModule,
    PartialsModule,
    BrowserAnimationsModule,
    MatchHeightModule,
    BreadcrumbModule,
    NgbModule,
    NgbNavModule,
    FormsModule,
    ReactiveFormsModule,
    AutocompleteLibModule,
    PageLoaderComponent,
    routing,
    RouterModule,
    // Settings modules
    SettingsModule.forRoot(ThemeSettingsConfig, MenuSettingsConfig),
    NgxSpinnerModule,
    LoadingBarRouterModule,
    BlockUIModule.forRoot({
      template: BlockTemplateComponent
    }),
    NgMultiSelectDropDownModule.forRoot(),
    ToastrModule.forRoot(),
    TableViewComponent,
    RatingComponent,
    AuthPermissionDirective,
    GoogleGeolocationComponent,
    GoogleMapComponent,
    RestrictToNumbersDirective,
    UrlValidatorDirective, 
    MediaPreviewComponent,
  ],
  providers: [
    AuthGuardCheck,
    PageAuthGuard,
    AuthGuard,
    AlertService,
    NavbarService,
    DeviceDetectorService,
    DataApiService,
    AuthService,
    AuthGuardUser,
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerGestureConfig
    },
    NgbCarouselConfig,
    NgbModalConfig,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [AuthServiceUser],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideLottieOptions({
      player: () => player,
    }),
  ],
  declarations: [
    AppComponent,
    PublicLayoutComponent,
    PrivateLayoutComponent,
    HeaderComponent,
    FullLayoutNavbarComponent,
    HeaderHorizontalComponent,
    HeaderVerticalComponent,
    FooterComponent,
    AppNavigationComponent,
    AlertComponent,
    RegisterComponent,
    SocialSigninComponent,
    LoginComponent,
    ChangelogComponent,
    VerticalnavComponent,
    HorizontalnavComponent,
    CustomizerComponent,
    HorizontalCustomizerComponent,
    BlockTemplateComponent,
    FullLayoutComponent,
    AddProductComponent,
    ViewProductComponent,
    ProductListComponent,
    CategoryListComponent,
    AddCategoryComponent,
    ViewCategoryComponent,
    ListRetailerComponent,
    AddRetailerComponent,
    FormModalComponent,
    UserListComponent,
    OfferListComponent,
    AddOfferComponent,
    OfferSectionComponent,
    ImageUploadComponent,
    MonthlyCreateComponent,
    MonthlyListComponent,
    FocusOnClickDirective,
  ],
  // entryComponents: [
  //   BlockTemplateComponent
  // ],
  bootstrap: [AppComponent],
  exports: [RouterModule],
})

export class AppModule {

}
